import { Component, input, output, OnInit, OnDestroy, computed, signal, effect } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Aggregations, Filter, FilterTypes, PriorityFilterId } from '../filters/filters';
import { FiltersComponent } from '../filters/filters.component';
import { Subject, takeUntil } from 'rxjs';
import {
  ListSelectionButtonComponent,
  ListSelectionGroup,
  ListSelectionOption,
} from '../list-selection-button/list-selection-button.component';
import { normalizeFilters } from '../filters/filter/filter-groups.helper';
import { ListSelectionButtonConfig } from '../list-selection-button/list-selection-button.model';

/**
 * Generic reusable filter + search bar + list-selection-button component.
 *
 * No services are injected. All data flow is via inputs/outputs.
 *
 * Inputs:
 *   filters           — initial Filter[] config
 *   aggregations      — Aggregations map fed back by the parent after a lookup call
 *   searchPlaceholder — placeholder text for the search input (default: 'Search')
 *   knownFilterKeys   — optional map of filter key -> field override
 *   listBtnLabel      — label for the list-selection-button (default: 'More Filters')
 *   listBtnIcon       — svgIcon name for the list-selection-button
 *
 * Outputs:
 *   filterChange — emits the built Filter[] payload whenever filters or search change
 *
 * Sync behaviour:
 *   - Selecting an option in the listbox adds that filter key to the active filters
 *     passed to cbre-filters.
 *   - Removing a filter chip in cbre-filters deselects the matching option in the
 *     listbox automatically (selectedValues is kept in sync via _activeFilterKeys).
 */
@Component({
  selector: 'cbre-dialog-filters-search',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FiltersComponent,
    ListSelectionButtonComponent,
  ],
  templateUrl: './dialog-filters-search.component.html',
  styleUrls: ['./dialog-filters-search.component.scss'],
})
export class DialogFiltersSearchComponent implements OnInit, OnDestroy {
  // ─── Inputs ──────────────────────────────────────────────────────────────────

  readonly initialFilters = input<Filter[]>([]);
  /**
   * Full pool of filters available for selection via the "+Add Filter" listbox.
   * When provided, listOptions is derived from this instead of initialFilters,
   * allowing the default active set to be a subset of the available filters.
   * If omitted, listOptions falls back to initialFilters (existing behaviour).
   */
  readonly allFilters = input<Filter[]>([]);
  readonly aggregations = input<Aggregations>({});
  readonly searchPlaceholder = input<string>('Search');
  readonly knownFilterKeys = input<Record<string, string>>({});
  readonly priorityFilterIds = input<PriorityFilterId>([]);
  readonly listBtnConfig = input<ListSelectionButtonConfig>({});

  // ─── Outputs ─────────────────────────────────────────────────────────────────

  readonly filterChange = output<Filter[]>();

  // ─── Internal state ───────────────────────────────────────────────────────────

  /** Working copy of the currently active filters passed to cbre-filters. */
  filters: Filter[] = [];

  /**
   * Computed array passed as [selectedValues] to list-selection-button.
   * Derived directly from _filters — no manual _syncActiveKeys() calls needed.
   */
  readonly selectedListValues = computed(() => this._activeFilterKeys());

  /**
   * Options derived from allFilters / initialFilters — shown in the list-selection-button popover.
   * Recomputed only when the relevant inputs change.
   */
  listOptions: ListSelectionOption[] | ListSelectionGroup = [];

  /** Flat pool cached so handleListSelectionChange doesn't re-flatten on every call. */
  private _flatPool: Filter[] = [];

  /** Signal-backed set of active filter keys — drives selectedListValues. */
  private readonly _activeFilterKeys = signal<string[]>([]);

  readonly searchCtrl = new FormControl<string>('');

  private readonly _destroy$ = new Subject<void>();

  constructor() {
    // Replaces ngOnChanges — reacts to signal input changes without lifecycle overhead.
    effect(() => {
      const allFilters = this.allFilters();
      const initialFilters = this.initialFilters();

      const source = allFilters?.length ? allFilters : initialFilters;
      const flatPool = this._flattenFilters(source);
      this._flatPool = flatPool;

      const poolByKey = new Map(flatPool.map(f => [String(f.key), f]));
      // Use the pool entry for structure/metadata but preserve the values from
      // initialFilters — the pool always has values:null (pristine), so replacing
      // entirely would discard restored filter values (e.g. after step back/forward).
      const resolvedDefaults = initialFilters.map(f => {
        const poolEntry = poolByKey.get(String(f.key));
        if (!poolEntry) return f;
        return { ...poolEntry, values: f.values };
      });
      const nextFilters = normalizeFilters(this._cloneFilters(resolvedDefaults)) as Filter[];

      const defaultKeys = new Set(initialFilters.map(f => String(f.key)));
      this.listOptions = this._buildListOptions(source, defaultKeys);

      this._setFilters(nextFilters);
      this._emitPayload(nextFilters);
    });
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // debounceTime is sufficient here; updateOn: 'blur' was removed to avoid
    // conflicting emission strategies on the same control.
    this.searchCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._emitPayload(this.filters));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ─── Public handlers ─────────────────────────────────────────────────────────

  /** Called when cbre-filters emits a new filter array (chip removed / filter changed). */
  handleFilterChange(changes: Filter[]): void {
    this._setFilters(changes);
    this._emitPayload(changes);
  }

  /** Called when cbre-filters emits individual field value changes. */
  handleValueChanges(changes: Record<string, any>): void {
    // Mutate values in place so external references (e.g. rowData bindings) stay in sync.
    this.filters.forEach(f => {
      if (f.type === FilterTypes.Group) {
        const associations = f.associations?.map(a => ({ ...(a as Filter), values: changes[(a as Filter).key] }));
        f.associations = associations as Filter[];
        f.values = associations?.map(a => this._toFilterQuery(a as Filter)) as any;
      } else {
        f.values = changes[f.key];
      }
    });
    this._emitPayload(this.filters);
  }

  /**
   * Called when the user selects / deselects options in the list-selection-button.
   * Uses the cached _flatPool to avoid re-flattening on every interaction.
   */
  // handleListSelectionChange(selectedKeys: string[]): void {
  //   const idSet = new Set(selectedKeys.map(k => String(k)));

  //   // kept: preserve existing filters in their current order (default order untouched)
  //   const kept = this.filters.filter(f => idSet.has(String(f.key)));
  //   const keptKeys = new Set(kept.map(f => String(f.key)));

  //   // added: newly selected filters not already in kept
  //   const added = this._flatPool.filter(f => idSet.has(String(f.key)) && !keptKeys.has(String(f.key)));

  //   // Deduplicate group filters — show one chip per group_key
  //   const deduped = this._deduplicateGroupFilters(added);

  //   // Append newly added filters after kept — do NOT reorder kept
  //   const next = [...kept, ...(normalizeFilters(deduped) as Filter[])];
  //   this._setFilters(next);
  //   this._emitPayload(next);
  // }

  handleListSelectionChange(selectedKeys: string[]): void {
    if (!selectedKeys?.length) {
      this._setFilters([]);
      this._emitPayload([]);
      return;
    }
    const idSet = new Set(selectedKeys.map(String));

    // Keys already covered by kept selectItemsInGroup groups (the group key + all association keys)
    const kept = (this.filters ?? []).filter(f => idSet.has(String(f.key)));
    const coveredByKept = new Set<string>();
    kept.forEach(f => {
      coveredByKept.add(String(f.key));
      if ((f as any).selectItemsInGroup && f.associations?.length) {
        (f.associations as Filter[]).forEach(a => coveredByKept.add(String(a.key)));
      }
    });

    // Only add flat-pool entries not already covered by a kept group
    const added = (this._flatPool ?? []).filter(f => idSet.has(String(f.key)) && !coveredByKept.has(String(f.key)));
    const normalizedAdded = normalizeFilters(this._deduplicateGroupFilters(added)) as Filter[];

    const next = [...kept, ...normalizedAdded];
    this._setFilters(next);
    this._emitPayload(next);
  }

  private _deduplicateGroupFilters(filters: Filter[]): Filter[] {
    const seenGroupKeys = new Set<string>();
    const result: Filter[] = [];
    for (const f of filters) {
      const gk = (f as any).group_key;
      const inGroup = (f as any).selectItemsInGroup;
      if (inGroup && gk) {
        if (seenGroupKeys.has(gk)) continue;
        seenGroupKeys.add(gk);
        // Use the first-encountered sibling (preserves selectedKeys order) as representative
        result.push(f);
      } else {
        result.push(f);
      }
    }
    return result;
  }

  handleRemoveFilter(filterKey: Record<string, any> | any): void {
    const key = typeof filterKey === 'object' && 'key' in filterKey ? filterKey.key : filterKey;
    const removedFilter = this.filters.find(f => f.key === key);
    const groupKey = (removedFilter as any)?.group_key;

    const updated = this.filters.filter(f => {
      if (f.key === key) return false;
      // If the removed filter belongs to a group, remove all siblings with same group_key
      if (groupKey && (f as any).group_key === groupKey) return false;
      return true;
    });

    this._setFilters(updated);
    this._emitPayload(updated);
  }

  handleSearchChange(event: KeyboardEvent): void {
    (event.target as HTMLElement).blur();
    this._emitPayload(this.filters);
  }

  clearSearch(): void {
    this.searchCtrl.setValue('');
  }

  // ─── Payload builder ─────────────────────────────────────────────────────────

  buildPayload(filters: Filter[] = this.filters): Filter[] {
    const ctrlSearchValue = this.searchCtrl.value?.trim() || null;

    const nextFilters: Filter[] = filters.map(f => {
      if (f.type === FilterTypes.Group && f.associations) {
        const assocValues = (f.associations as Filter[]).map(a => this._toFilterQuery({ ...a, values: a.values }));
        const { ...rest } = f as any;
        return { ...rest, values: assocValues } as Filter;
      }
      return this._toFilterQuery(f);
    });

    return [
      ...nextFilters,
      { type: FilterTypes.MultiField, values: ctrlSearchValue, dataType: 'text', typeAhead: true, field: [] } as any,
    ];
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  /** Sets the active filters and syncs the list-button selection state in one call. */
  private _setFilters(filters: Filter[]): void {
    this.filters = filters;
    const keys: any[] = [];
    filters.forEach(f => {
      keys.push(f.key);
      const isGroup = (f as any).selectItemsInGroup;
      if (isGroup && (f as any).associations?.length) {
        (f as any).associations.forEach((a: any) => {
          if (this._flatPool.find(g => g.key == a.key)?.selectItemsInGroup) {
            keys.push(a.key);
          }
        });
      }
    });
    this._activeFilterKeys.set(keys);
  }

  private _emitPayload(filters: Filter[]): void {
    this.filterChange.emit(this.buildPayload(filters));
  }

  private _toFilterQuery = (f: Filter): Filter => {
    const { key, type, dataType, field, values, includeEmpty, excludeEmpty, excludeSelection, compoundFilters } = f;
    const overrides = this.knownFilterKeys();
    const keyOverride = overrides[key] != null ? { key: overrides[key] } : {};
    return {
      ...keyOverride,
      type,
      dataType,
      field,
      values,
      key,
      includeEmpty,
      excludeEmpty,
      excludeSelection,
      compoundFilters,
    } as Filter;
  };

  sortByPriority(filters: Filter[], filterIds: PriorityFilterId): Filter[] {
    return [...filters].sort((a, b) => filterIds[a.key].priorityLevel - filterIds[b.key].priorityLevel);
  }

  private _sortByOrder(filterA: Filter, filterB: Filter, filterIds: PriorityFilterId): number {
    return (filterIds[filterA.key as number]?.order ?? 0) - (filterIds[filterB.key as number]?.order ?? 0);
  }

  private _applyPriorityOrder(filters: Filter[]): Filter[] {
    const filterIds = this.priorityFilterIds();
    if (!filterIds || !Object.keys(filterIds).length) {
      return filters;
    }
    const highPriority = filters.filter(f => filterIds[f.key as number]?.isPriority);
    const rest = filters.filter(f => !filterIds[f.key as number]?.isPriority);
    return [
      ...this.sortByPriority(highPriority, filterIds),
      ...rest.sort((a, b) => this._sortByOrder(a, b, filterIds)),
    ];
  }

  private _cloneFilters(filters: Filter[]): Filter[] {
    return filters.map(f => ({
      ...f,
      associations: f.associations ? [...f.associations] : undefined,
    }));
  }

  /** Flattens a flat or grouped (label/items) filter array into a single Filter[]. */
  private _flattenFilters(filters: Filter[] | any[]): Filter[] {
    return (filters as any[]).flatMap((entry: any) => (entry.items ? entry.items : [entry]));
  }

  /**
   * Builds listOptions from the source array.
   * Default filters (already active) and excluded filters are marked `disabled: true`
   * for the listbox only — cbre-filters never receives these options directly.
   */
  private _buildListOptions(
    source: Filter[] | any[],
    defaultKeys: Set<string>
  ): ListSelectionOption[] | ListSelectionGroup {
    const mapOption = (f: Filter): Filter => ({
      ...f,
      disabled: defaultKeys.has(String(f.key)) || !!f.excludeSelection,
    });
    return (source as any[]).map((entry: any) =>
      entry.items ? { ...entry, items: (entry.items as Filter[]).map(mapOption) } : mapOption(entry)
    ) as any;
  }
}
